import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import getTime from 'date-fns/getTime';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import {
  FilterMatchStrategy,
  FilterOperatorDate,
  TableColumnValueType,
  type TableFilter,
  type TableRow,
} from 'src/generated/graphql';
import {
  type CompareCondition,
  CompareOperator,
  type Condition,
  type NumberCondition,
  NumberOperator,
  type StringCondition,
  StringOperator,
} from '../BoardFilter/types';

const fieldTypeCompare: any = {
  [FilterMatchStrategy.Or]: (proValue: any, inValues: any[]) => {
    if (inValues?.includes('EMPTY') && !proValue) {
      return true;
    }
    return inValues?.some((v) => v === proValue);
  },
  [FilterMatchStrategy.And]: (proValue: any, inValues: any[]) => {
    if (inValues?.includes('EMPTY') && !proValue) {
      return true;
    }
    return inValues?.every((v) => v === proValue);
  },
};

const fieldTypeArrayCompare: any = {
  [FilterMatchStrategy.Or]: (proValues: any[], inValues: any[]) => {
    const set = new Set(Array.isArray(proValues) ? proValues : [proValues]);
    if (!proValues?.length) {
      return true;
    }
    return inValues?.some((v) => set.has(v));
  },
  [FilterMatchStrategy.And]: (proValues: any[], inValues: any[]) => {
    const set = new Set(Array.isArray(proValues) ? proValues : [proValues]);
    if (!proValues?.length) {
      return true;
    }
    return inValues?.every((v) => set.has(v));
  },
};

const stringCompare: any = {
  [StringOperator.Contains]: (value: string) => ({
    $regex: value,
    $options: 'igm',
  }),
  [StringOperator.NotContains]: (value: string) => ({
    $regex: `^((?!${value}).)*$`,
    $options: 'igm',
  }),
  [StringOperator.StartsWith]: (value: string) => ({
    $regex: `^${value}+.`,
    $options: 'igm',
  }),
  [StringOperator.EndsWith]: (value: string) => ({
    $regex: `.+${value}$`,
    $options: 'igm',
  }),
};

const numberOperatorCompare: any = {
  [NumberOperator.Equals]: (a: number, b: number) => a === b,
  [NumberOperator.NotEquals]: (a: number, b: number) => a !== b,
  [NumberOperator.Max]: (a: number, b: number) => a < b,
  [NumberOperator.Min]: (a: number, b: number) => a > b,
  [NumberOperator.MaxEquals]: (a: number, b: number) => a <= b,
  [NumberOperator.MinEquals]: (a: number, b: number) => a >= b,
};

const boolOperatorCompare: any = {
  [NumberOperator.Equals]: (a: boolean, b: boolean) => a === b,
  [NumberOperator.NotEquals]: (a: number, b: number) =>
    a && b ? a !== b : true,
};

function parseDate(
  value: FilterOperatorDate,
  range?: [string, string],
  dateParam?: string,
): [number, number] {
  const now = new Date();

  const periods: { [s: string]: () => [Date, Date] } = {
    [FilterOperatorDate.Date]: () => {
      if (!dateParam) {
        throw new Error('Missing date');
      }
      const d: Date = new Date(dateParam);
      return [startOfDay(d), endOfDay(d)];
    },
    [FilterOperatorDate.Range]: () => {
      if (!range) {
        throw new Error('Missing range dates');
      }
      return [startOfDay(new Date(range[0])), endOfDay(new Date(range[1]))];
    },
    [FilterOperatorDate.Today]: () => [startOfDay(now), endOfDay(now)],
    [FilterOperatorDate.Yesterday]: () => {
      const date: Date = subDays(now, 1);
      return [startOfDay(date), endOfDay(date)];
    },
    [FilterOperatorDate.Tomorrow]: () => {
      const date: Date = addDays(now, 1);
      return [startOfDay(date), endOfDay(date)];
    },
    [FilterOperatorDate.NextSevenDays]: () => {
      const date: Date = addDays(now, 7);
      return [startOfDay(now), endOfDay(date)];
    },
    [FilterOperatorDate.LastSevenDays]: () => {
      const date: Date = subDays(now, 7);
      return [startOfDay(date), endOfDay(now)];
    },
    [FilterOperatorDate.CurrentWeek]: () => [
      startOfDay(startOfWeek(now)),
      endOfDay(endOfWeek(now)),
    ],
    [FilterOperatorDate.LastWeek]: () => {
      const date: Date = subDays(now, 7);
      return [startOfDay(startOfWeek(date)), endOfDay(endOfWeek(date))];
    },
    [FilterOperatorDate.NextWeek]: () => {
      const date: Date = addDays(now, 7);
      return [startOfDay(startOfWeek(date)), endOfDay(endOfWeek(date))];
    },
    [FilterOperatorDate.CurrentMonth]: () => [
      startOfDay(startOfMonth(now)),
      endOfDay(endOfMonth(now)),
    ],
    [FilterOperatorDate.LastMonth]: () => {
      const date: Date = subMonths(now, 1);
      return [startOfDay(startOfMonth(date)), endOfDay(endOfMonth(date))];
    },
    [FilterOperatorDate.NextMonth]: () => {
      const date: Date = addMonths(now, 1);
      return [startOfDay(startOfMonth(date)), endOfDay(endOfMonth(date))];
    },
    default: () => {
      throw new Error('Invalid period');
    },
  };

  const [start, end]: [Date, Date] = (periods[value] || periods.default)();

  return [getTime(start), getTime(end)];
}

const defaultValueExtractor = (object: TableRow, prop: any) =>
  object.columns.find((c) => c.columnId === prop)?.value;

type compareFn<T> = [T | string, (v: any) => boolean];

const getSelectCompare = <T>(condition: CompareCondition): compareFn<T> => {
  const { matchStrategy, operator } = condition;
  const compare = (v: any[]) =>
    fieldTypeArrayCompare[matchStrategy || FilterMatchStrategy.Or](
      v,
      condition.value,
    );
  if (operator === CompareOperator.NotIncludes) {
    return [condition.columnId, (v) => !compare(v)];
  }
  return [condition.columnId, (v) => compare(v)];
};

const getNumberCompare = <T>(condition: NumberCondition): compareFn<T> => {
  const { operator, value } = condition;
  const compare = (v: number | any[]) =>
    operator === NumberOperator.Includes ||
    operator === NumberOperator.NotIncludes
      ? fieldTypeCompare[FilterMatchStrategy.Or](v as any[], value as any)
      : numberOperatorCompare[operator](v as number, value as number);
  if (operator === NumberOperator.NotIncludes) {
    return [condition.columnId, (v) => !compare(v)];
  }
  return [condition.columnId, (v) => compare(v)];
};

const getBooleanCompare = <T>(condition: NumberCondition): compareFn<T> => {
  const { operator } = condition;
  const compare = (v: number) =>
    boolOperatorCompare[operator](v, condition.value);
  return [condition.columnId, (v) => compare(v)];
};

const getStringCompare = <T>(condition: StringCondition): compareFn<T> => {
  const { operator, value } = condition;
  const regexFunction = (v: string) => {
    const { $regex, $options } = stringCompare[operator](value);
    const reg = new RegExp($regex, $options);
    return !!String(v).match(reg);
  };
  const compare = (v: string | any[]) => regexFunction(v as string);
  if (operator === StringOperator.NotContains) {
    return [condition.columnId, (v) => !compare(v)];
  }
  return [condition.columnId, (v) => compare(v)];
};

const getDateCompare = <T>(
  condition: Condition & {
    date?: string;
    range?: [string, string];
  },
): compareFn<T> => {
  const compare = (d: string) => {
    const [start, end] = parseDate(
      condition.operator as FilterOperatorDate,
      condition.range,
      condition.date,
    );
    const _d = getTime(parseISO(d));
    return (
      numberOperatorCompare[NumberOperator.MinEquals](_d, start) &&
      numberOperatorCompare[NumberOperator.MaxEquals](_d, end)
    );
  };
  if (condition.operator === CompareOperator.NotEqual) {
    return [condition.columnId, (v) => !compare(v)];
  }
  return [condition.columnId, (v) => compare(v)];
};

export function buildMatcher<T extends object>(filter: TableFilter) {
  const matchers = filter.conditions.map<compareFn<T>>(
    // @ts-ignore
    (condition) => {
      if (condition.valueType === TableColumnValueType.Select) {
        return getSelectCompare(condition as CompareCondition);
      }
      if (condition.valueType === TableColumnValueType.Number) {
        return getNumberCompare(condition as NumberCondition);
      }
      if (condition.valueType === TableColumnValueType.String) {
        return getStringCompare(condition as StringCondition);
      }
      if (condition.valueType === TableColumnValueType.Date) {
        return getDateCompare(condition as Condition);
      }
      if (condition.valueType === TableColumnValueType.Bool) {
        return getBooleanCompare(condition as NumberCondition);
      }
    },
  );
  return (obj: T) =>
    matchers[
      filter.matchStrategy === FilterMatchStrategy.And ? 'every' : 'some'
    ](([prop, match]) => {
      const value = defaultValueExtractor(obj as TableRow, prop);
      return match(value);
    });
}
